// Inbox de notificacoes: leitura via RLS (notifications + receipts), escrita
// via RPC. Atualiza ao vivo pelo Realtime (alertas de integracao).

import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { buildInbox, type Inbox, type NotificationRow, type ReceiptRow } from "@/lib/notifications";

const INBOX_KEY = ["notifications-inbox"] as const;
const INBOX_LIMIT = 50;

async function fetchInbox(): Promise<Inbox> {
  const [rows, receipts] = await Promise.all([
    supabase
      .from("notifications")
      .select("id, tenant_id, kind, severity, title, body_md, image_url, action_url, published_at, resolved_at")
      .order("published_at", { ascending: false })
      .limit(INBOX_LIMIT),
    supabase.from("notification_receipts").select("notification_id, read_at, dismissed_at"),
  ]);
  if (rows.error) throw rows.error;
  if (receipts.error) throw receipts.error;
  return buildInbox((rows.data ?? []) as NotificationRow[], (receipts.data ?? []) as ReceiptRow[]);
}

/** Assinatura Realtime da inbox. Montar uma vez no layout (IntegrationAlerts). */
export function useInboxRealtime(): void {
  const qc = useQueryClient();
  useEffect(() => {
    const channel = supabase
      .channel("notifications-inbox")
      .on("postgres_changes", { event: "*", schema: "public", table: "notifications" }, () => {
        void qc.invalidateQueries({ queryKey: INBOX_KEY });
      })
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [qc]);
}

export function useInbox() {
  return useQuery<Inbox, Error>({
    queryKey: INBOX_KEY,
    queryFn: fetchInbox,
    staleTime: 30_000,
    refetchInterval: 120_000,
  });
}

function useInboxRpc<TArg>(fn: string, toArgs: (arg: TArg) => Record<string, unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (arg: TArg) => {
      const { error } = await supabase.rpc(fn, toArgs(arg));
      if (error) throw error;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: INBOX_KEY }),
  });
}

export const useMarkNotificationRead = () =>
  useInboxRpc<string>("mark_notification_read", (id) => ({ p_notification_id: id }));
export const useDismissNotification = () =>
  useInboxRpc<string>("dismiss_notification", (id) => ({ p_notification_id: id }));
export const useMarkAllNotificationsRead = () => useInboxRpc<void>("mark_all_notifications_read", () => ({}));
